import {
	RegisteredApplications,
	RenderComponentOrLoading,
	getLibraryList,
	getRegionSuffix,
	selectApplicationName,
	useIsFeatureEnabled,
	useSelectLibraries,
	useSettings,
	useTenantLibraries,
} from '@innovyze/stylovyze';
import React, { useEffect } from 'react';
import { getCurrentCompanySettings, getTenantSchedules } from '@Actions';
import { useDispatch, useSelector } from 'react-redux';

import DateAndTime from './components/DateAndTime';
import DefaultCurrencySelection from './components/DefaultCurrencySelection';
import DefaultImportCoordinateSystem from './components/DefaultImportCoordinateSystem';
import DefaultLocation from './components/DefaultLocation';
import DefaultMapSettings from './components/DefaultMapSettings';
import DefaultScenario from './components/DefaultScenario';
import DefaultServiceLevels from './components/DefaultServiceLevels';
import DefaultSystemMeasurement from './components/DefaultSystemMeasurement';
import { FLAGS } from '@Types';
import Libraries from './components/Libraries';
import StyledGeneralSettings from './GeneralSettings.styles';
import { getHourCycleString } from './utils/GeneralSettingsUtils';
import { getThemeList } from '@Actions/mapManagement';
import { useIsGeneralSettingsInitialized } from '@Selectors';
import { Formik } from 'formik';

export interface Option {
	value: string;
	key: string;
}

export default function GeneralSettings() {
	const dispatch = useDispatch();
	const { companySettings } = useSettings();

	// selectors
	const applicationName = useSelector(selectApplicationName);
	const isGeneralSettingsInitialized = useIsGeneralSettingsInitialized();

	const isAdminOrMainApp =
		applicationName === RegisteredApplications.APP_ADMIN ||
		applicationName === RegisteredApplications.APP_ONU_MAIN;
	const isAdminOrAssetApp =
		applicationName === RegisteredApplications.APP_ADMIN ||
		applicationName === RegisteredApplications.APP_AM_MAIN;

	const { initialized: isLibraryInitialized } = useSelectLibraries();

	const currentSelectionEnabled = useIsFeatureEnabled(
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		FLAGS.CURRENCY_SELECTION,
	);

	// EFFECTS
	useEffect(() => {
		dispatch(getCurrentCompanySettings({}));
	}, []);

	useEffect(() => {
		dispatch(getLibraryList());
	}, []);

	useEffect(() => {
		dispatch(getThemeList());
	}, []);

	useEffect(() => {
		if (isAdminOrMainApp) {
			const tenantId = companySettings.organization ?? '';
			const defaultRegion = 'us';
			const tenantRegion = getRegionSuffix() || defaultRegion;

			dispatch(
				getTenantSchedules({
					tenant_id: tenantId,
					tenant_region: tenantRegion,
				}),
			);
		}
	}, [isAdminOrMainApp]);

	const { selectedLibraries } = useTenantLibraries();
	const updatedCompanySettings = getHourCycleString({
		...companySettings,
		allowedLibrariesByAdmin: selectedLibraries.map(
			library => library.tenantId,
		),
	});

	return (
		<RenderComponentOrLoading
			loading={!isGeneralSettingsInitialized || !isLibraryInitialized}>
			<Formik
				initialValues={updatedCompanySettings}
				onSubmit={() => void 0}>
				{() => (
					<StyledGeneralSettings>
						<DateAndTime />
						<DefaultLocation />
						<DefaultSystemMeasurement />
						<DefaultMapSettings />
						{isAdminOrMainApp && <DefaultServiceLevels />}
						{isAdminOrAssetApp && <DefaultImportCoordinateSystem />}
						{currentSelectionEnabled && (
							<DefaultCurrencySelection />
						)}
						{isAdminOrMainApp && <DefaultScenario />}
						{isAdminOrMainApp && <Libraries />}
					</StyledGeneralSettings>
				)}
			</Formik>
		</RenderComponentOrLoading>
	);
}
